import { documentDownloadStarted } from '../store/printing/print.actions';
import { printDocumentRequest } from '../store/shipstation-connect/ss-connect.actions';
import Global from '../store/global-selectors';
import { get } from 'lodash-es';
import {
  DOWNLOAD_PRINT_SETTING_NAME,
  PREVIEW_PRINT_SETTING_NAME,
  ALWAYS_PROMPT_PRINT_SETTING_NAME,
} from '../pages/settings/printing/constants';
import { DocumentType } from '../enums/document-type';
import { printJobFactory } from '../store/printing/async-printing/print-job-factory';

export const isPrintSettingSetToPreview = (printSetting) => {
  // Unset print setting is defaulted to "always prompt"
  if (!printSetting) {
    return true;
  }

  // If the print setting has an id, it is for a printer
  if (printSetting.id) {
    return false;
  }

  // "preview" and "always prompt" are treated the same
  return printSetting.name === PREVIEW_PRINT_SETTING_NAME || printSetting.name === ALWAYS_PROMPT_PRINT_SETTING_NAME;
};

export const isPrintSettingSetToDownload = (printSetting) => {
  return printSetting && printSetting.name === DOWNLOAD_PRINT_SETTING_NAME && !printSetting.id;
};

/**
 * @param {Blob} blob
 * @param {string} fileName
 */
export const downloadFileFromBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export class DocumentService {
  static getPrintDocumentActions(printSetting, documentType, documentIds, templateId, printUrl) {
    const printJob = printJobFactory({
      documentTypeIdentifier: documentType.identifier,
      printer: printSetting,
      ids: documentIds,
      downloadUrl: printUrl,
      templateId,
    });

    const markDocumentPrintedAction = documentType.getMarkPrintedAction(documentIds);

    if (markDocumentPrintedAction == null) {
      return [printDocumentRequest(printJob)];
    }

    return [printDocumentRequest(printJob), markDocumentPrintedAction];
  }

  static getDownloadDocumentActions(documentType, documentIds) {
    const markDocumentPrintedAction = documentType.getMarkPrintedAction(documentIds);

    if (markDocumentPrintedAction == null) {
      return [documentDownloadStarted(documentType.identifier, documentIds.length)];
    }

    return [documentDownloadStarted(documentType.identifier, documentIds.length), markDocumentPrintedAction];
  }

  static getDefaultPrintSettingForDocumentType(state, documentType, shouldReturnDownloadSetting = false) {
    if (!documentType) {
      return;
    }

    const documentPrintSetting = get(Global.selectPrintSettingsPrintSettings(state), documentType.printSettingKey);

    if (shouldReturnDownloadSetting || !isPrintSettingSetToDownload(documentPrintSetting)) {
      return documentPrintSetting;
    }
  }

  static getExportDocumentActions(downloadUrl) {
    window.open(downloadUrl);
  }

  static downloadFile(url, fileName) {
    const anch = document.createElement('a');
    anch.href = url;
    // spec suggests download value will be the saved file name but something else is overriding it
    anch.download = fileName ?? '';
    document.body.appendChild(anch);
    anch.click();
    document.body.removeChild(anch);
  }

  static getDocumentTypeEnumValueFromIdentifier(documentTypeIdentifier) {
    return Object.values(DocumentType).find((doc) => doc.identifier === documentTypeIdentifier);
  }

  // In several cases printSettingKeys are shared across multiple DocumentTypes
  // Callers of this method should understand that they will get the first matching enum value
  static getDocumentTypeEnumValueFromPrintSettingKey(printSettingKey) {
    return Object.values(DocumentType).find((doc) => doc.printSettingKey === printSettingKey);
  }
}
